import type { RouteTree } from './types';

/**
 * THE route tree. Every navigation surface and the router derive from this constant.
 *
 * To add a page: add one entry (with `page` for a real screen, or `plan` for an honest
 * placeholder). Nothing else needs touching — the tests in tree.test.ts prove that the
 * sidebar, bottom bar, breadcrumb and router all pick a new entry up.
 *
 * Milestone tags in `plan` name the task that fills the slot (A3, B1, C2, M3 …).
 */
export const ROUTE_TREE: RouteTree = [
  {
    id: 'home',
    label: 'Home',
    path: '',
    icon: 'home',
    page: () => import('../pages/HomePage'),
  },
  {
    id: 'data',
    label: 'Data',
    path: 'data',
    icon: 'data',
    group: 'workspace',
    children: [
      {
        id: 'data.instruments',
        label: 'Instruments',
        path: 'instruments',
        icon: 'list',
        page: () => import('../pages/InstrumentsPage'),
      },
      {
        id: 'data.ingestion',
        label: 'Ingestion',
        path: 'ingestion',
        icon: 'play',
        page: () => import('../pages/IngestionPage'),
      },
      {
        id: 'data.jobs',
        label: 'Jobs',
        path: 'jobs',
        icon: 'box',
        page: () => import('../pages/JobsPage'),
        children: [
          {
            id: 'data.jobs.detail',
            label: 'Job',
            path: ':id',
            hidden: true,
            page: () => import('../pages/JobDetailPage'),
          },
        ],
      },
      {
        id: 'data.quality',
        label: 'Quality',
        path: 'quality',
        icon: 'check',
        page: () => import('../pages/QualityPage'),
      },
      {
        id: 'data.calendar',
        label: 'Calendar',
        path: 'calendar',
        icon: 'calendar',
        page: () => import('../pages/CalendarPage'),
      },
    ],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    path: 'analysis',
    icon: 'brain',
    status: 'soon',
    children: [
      {
        id: 'analysis.labeling',
        label: 'Labeling',
        path: 'labeling',
        icon: 'pen',
        page: () => import('../pages/LabelingPage'),
      },
      {
        id: 'analysis.replay',
        label: 'Replay',
        path: 'replay',
        icon: 'play',
        page: () => import('../pages/ReplayPage'),
      },
      {
        id: 'analysis.quality',
        label: 'Label quality',
        path: 'label-quality',
        icon: 'check',
        page: () => import('../pages/LabelQualityPage'),
      },
      {
        id: 'analysis.datasets',
        label: 'Datasets',
        path: 'datasets',
        icon: 'box',
        plan: [
          'C1 Point-in-time feature pipeline',
          'C2 Dataset builder, walk-forward splits',
          'C3 Label outcome enrichment',
        ],
      },
      {
        id: 'analysis.exits',
        label: 'Exit policies',
        path: 'exit-policies',
        icon: 'chart',
        plan: ['D1 Bar-by-bar execution simulator', 'D2 Trailing stop vs fixed take-profit sweep'],
      },
      {
        id: 'analysis.models',
        label: 'Models',
        path: 'models',
        icon: 'brain',
        plan: [
          'E2 Gradient-boosting baseline',
          'E3 Similarity retrieval over labelled setups',
          'E4 Claude agent analyzer',
        ],
      },
      {
        id: 'analysis.evaluation',
        label: 'Evaluation',
        path: 'evaluation',
        icon: 'check',
        plan: ['E1 The analyzer interface', 'E5 Evaluation harness — human vs model vs floor'],
      },
    ],
  },
  {
    id: 'trade',
    label: 'Trade',
    path: 'trade',
    icon: 'trade',
    status: 'soon',
    children: [
      {
        id: 'trade.paper',
        label: 'Paper',
        path: 'paper',
        icon: 'shield',
        env: 'paper',
        children: [
          {
            id: 'trade.paper.positions',
            label: 'Positions',
            path: 'positions',
            plan: ['M3 Broker adapter', 'M3 Paper account positions'],
          },
          {
            id: 'trade.paper.orders',
            label: 'Orders',
            path: 'orders',
            plan: ['M3 Order routing, paper'],
          },
          {
            id: 'trade.paper.history',
            label: 'History',
            path: 'history',
            plan: ['M3 Paper trade history'],
          },
        ],
      },
      {
        id: 'trade.live',
        label: 'Live',
        path: 'live',
        icon: 'shield',
        env: 'live',
        children: [
          {
            id: 'trade.live.positions',
            label: 'Positions',
            path: 'positions',
            plan: ['M3 Live positions — real money'],
          },
          {
            id: 'trade.live.orders',
            label: 'Orders',
            path: 'orders',
            plan: ['M3 Live order routing — real money'],
          },
          {
            id: 'trade.live.history',
            label: 'History',
            path: 'history',
            plan: ['M3 Live trade history'],
          },
        ],
      },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    path: 'help',
    icon: 'help',
    group: 'system',
    page: () => import('../pages/HelpPage'),
  },
  {
    id: 'admin',
    label: 'Admin',
    path: 'admin',
    icon: 'admin',
    group: 'system',
    // Display concern only (the API enforces on its own). One tier for now: the
    // superadmin bootstrapped in Milestone 1. Split when a second role has a member.
    minRole: 'superadmin',
    children: [
      {
        id: 'admin.users',
        label: 'Users & roles',
        path: 'users',
        icon: 'users',
        status: 'soon',
        plan: ['Roles beyond superadmin', 'Per-user job visibility'],
      },
      {
        id: 'admin.connections',
        label: 'Connections',
        path: 'connections',
        icon: 'plug',
        page: () => import('../pages/ConnectionsPage'),
      },
      {
        id: 'admin.system',
        label: 'System health',
        path: 'system',
        icon: 'pulse',
        page: () => import('../pages/SystemHealthPage'),
      },
      {
        id: 'admin.audit',
        label: 'Audit log',
        path: 'audit',
        icon: 'shield',
        status: 'soon',
        plan: ['Who enqueued what, and when'],
      },
    ],
  },
];
